import {
  Autocomplete,
  Button,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { When } from '@mynt/components/When.tsx'
import { Controller, useForm } from 'react-hook-form'
import { BillingFrequency, type Country, type CreateTemplateFormData, type Currency, type Partner, SupportType } from '../types'

export interface TemplateFormData {
  options: CreateTemplateOptions
  onSubmit: (formData: CreateTemplateFormData) => void
  isPatching: boolean
}

interface Option {
  label: string
  value: string
}

interface TemplateOption {
  name: string
  value: string
}

export interface CreateTemplateOptions {
  partners: string[]
  currencies: string[]
  countries: Option[]
  templates: TemplateOption[]
}

export const TemplateForm = ({ options, onSubmit, isPatching }: TemplateFormData) => {
  const form = useForm<CreateTemplateFormData>({
    defaultValues: {
      name: '',
      externalName: '',
      eligibility: {
        partners: { type: SupportType.ALL, values: [] },
        currencies: { type: SupportType.ALL, values: [] },
        countries: { type: SupportType.ALL, values: [] },
        billingFrequency: BillingFrequency.MONTHLY,
      },
      copyFromExisting: false,
      templateToCopy: '',
      navigateToTemplate: true,
    },
  })

  const handleSubmit = (data: CreateTemplateFormData) => {
    onSubmit(data)
  }

  const templateOptions = [...options.templates.map((template) => ({ label: template.name, value: template.value }))]

  const partnerOptions = [
    { label: 'All Partners', value: SupportType.ALL },
    ...options.partners.map((partner) => ({ label: partner, value: partner })),
  ]
  const countryOptions = [
    { label: 'All Countries', value: SupportType.ALL },
    ...options.countries.map((country) => ({ label: country.label, value: country.value })),
  ]

  const currencyOptions = [
    { label: 'All Currencies', value: SupportType.ALL },
    ...options.currencies.map((currency) => ({ label: currency, value: currency })),
  ]

  const billingIntervalOptions = [
    { label: 'Monthly', value: BillingFrequency.MONTHLY },
    { label: 'Yearly', value: BillingFrequency.YEARLY },
  ]

  return (
    <Stack spacing={2}>
      <Stack
        sx={{
          gap: 3,
        }}
      >
        <Typography variant="h2">Create new template</Typography>
        <Typography variant="p2Medium">Template details</Typography>
      </Stack>
      <form onSubmit={form.handleSubmit(handleSubmit, console.error)}>
        <Stack
          sx={{
            gap: 3,
          }}
        >
          <TextField fullWidth id="template-name" {...form.register('name')} label="Template Name" variant="outlined" />
          <TextField fullWidth id="external-name" {...form.register('externalName')} label="External Name" variant="outlined" />
          <Stack
            sx={{
              gap: 2,
              flexDirection: 'row',
            }}
          >
            <Autocomplete
              fullWidth
              id="eligibility-partners"
              options={partnerOptions}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              defaultValue={partnerOptions[0]}
              disableClearable
              onChange={(_event, selected) => {
                if (selected?.value === SupportType.ALL) {
                  form.setValue('eligibility.partners.type', SupportType.ALL)
                  form.setValue('eligibility.partners.values', [])

                  return
                }

                form.setValue('eligibility.partners.type', SupportType.SPECIFIC)
                form.setValue('eligibility.partners.values', [selected?.value as Partner])
              }}
              renderInput={(params) => <TextField {...params} label="Partners" variant="outlined" />}
            />
            <FormControl fullWidth>
              <InputLabel id="eligibility-currencies-label">Currencies</InputLabel>
              <Select
                id="eligibility-currencies"
                label="Currencies"
                variant="outlined"
                labelId="eligibility-currencies-label"
                defaultValue={currencyOptions[0].value}
                onChange={(event) => {
                  const value = event.target.value as Currency

                  if (value === SupportType.ALL) {
                    form.setValue('eligibility.currencies.type', SupportType.ALL)
                    form.setValue('eligibility.currencies.values', [])

                    return
                  }

                  form.setValue('eligibility.currencies.type', SupportType.SPECIFIC)
                  form.setValue('eligibility.currencies.values', [value])
                }}
              >
                {currencyOptions.map(({ label, value }) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          <Stack
            sx={{
              gap: 2,
              flexDirection: 'row',
            }}
          >
            <Autocomplete
              fullWidth
              id="eligibility-countries"
              options={countryOptions}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              defaultValue={countryOptions[0]}
              disableClearable
              onChange={(_event, selected) => {
                if (selected?.value === SupportType.ALL) {
                  form.setValue('eligibility.countries.type', SupportType.ALL)
                  form.setValue('eligibility.countries.values', [])

                  return
                }

                form.setValue('eligibility.countries.type', SupportType.SPECIFIC)
                form.setValue('eligibility.countries.values', [selected?.value as Country])
              }}
              renderInput={(params) => <TextField {...params} label="Countries" variant="outlined" />}
            />
            <FormControl fullWidth>
              <InputLabel id="eligibility-billing-interval-label">Billing Interval</InputLabel>
              <Controller
                control={form.control}
                name="eligibility.billingFrequency"
                rules={{ required: 'Please select a billing interval' }}
                render={({ field }) => (
                  <Select
                    id="eligibility-billing-interval"
                    label="Billing Interval"
                    variant="outlined"
                    labelId="eligibility-billing-interval-label"
                    onChange={(event) => field.onChange(event.target.value as BillingFrequency)}
                    value={field.value}
                  >
                    {billingIntervalOptions.map(({ label, value }) => (
                      <MenuItem key={value} value={value}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
          </Stack>
          <Controller
            control={form.control}
            name="copyFromExisting"
            render={({ field }) => (
              <FormControlLabel
                sx={{ margin: 0, alignItems: 'center', justifyContent: 'space-between' }}
                labelPlacement="start"
                control={
                  <Switch
                    role="switch"
                    checked={field.value}
                    onChange={(event) => {
                      field.onChange(event.target.checked)
                      if (!event.target.checked) {
                        form.setValue('templateToCopy', '')
                      }
                    }}
                  />
                }
                label={
                  <Typography variant="p4Regular" color="textSecondary">
                    Copy features and prices from existing template
                  </Typography>
                }
              />
            )}
          />
          <When is={form.watch('copyFromExisting')}>
            <Controller
              control={form.control}
              name="templateToCopy"
              rules={{ required: 'Please select a template to copy from' }}
              render={({ field, fieldState }) => (
                <Autocomplete
                  fullWidth
                  id="template-to-copy"
                  options={templateOptions}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  value={templateOptions.find((o) => o.value === field.value) ?? undefined}
                  disableClearable
                  onChange={(_event, selected) => {
                    if (selected) {
                      field.onChange(selected.value)
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Templates"
                      variant="outlined"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              )}
            />
          </When>
          <Controller
            control={form.control}
            name="navigateToTemplate"
            render={({ field }) => (
              <FormControlLabel
                sx={{ margin: 0, alignItems: 'center', justifyContent: 'space-between' }}
                labelPlacement="start"
                control={<Switch role="switch" checked={field.value} onChange={(event) => field.onChange(event.target.checked)} />}
                label={
                  <Typography variant="p4Regular" color="textSecondary">
                    Navigate to template after creation
                  </Typography>
                }
              />
            )}
          />
          <Button loading={isPatching} fullWidth type="submit" variant="contained" color="primary">
            Create
          </Button>
        </Stack>
      </form>
    </Stack>
  )
}
